import "./ResearchArticle.scss";
import { useTheme } from "../../context/ThemeContext";
import { useEffect } from "react";
import blueSquare from "../../assets/icons/type-squares/blue-square.svg";
import blackBackArrow from "../../assets/icons/arrows/black-back-arrow.svg";
import { Link } from "react-router-dom";

function SeeFar({ CLOUDFRONT_URL }) {
  const { setThemeByName } = useTheme();

  useEffect(() => {
    setThemeByName("theme2");

    return () => {
      setThemeByName("default");
    };
  }, [setThemeByName]);

  return (
    <>
      <div className="research-article">
        <div className="research-article__back-link-container">
          <Link to="/research" className="research-article__back-link">
            <img
              src={blackBackArrow}
              alt=""
              className="research-article__back-link-arrow"
            />
            <div className="research-article__back-link-text">
              Back to all articles
            </div>
          </Link>
        </div>
        <div className="research-article__container">
          <div className="research-article__info">
            <img
              src={blueSquare}
              alt=""
              className="research-article__info-square"
            />
            <div className="research-article__info-type"></div>
            <div className="research-article__info-date">
              June 10th, 2024
            </div>
          </div>
          <div className="research-article__title">
            SeeFar: A satellite agnostic multi-resolution dataset for geospatial foundation models
          </div>
          <div className="research-article__content">
            <div className="research-article__text">
              <div className="research-article__paragraphs">
                <div className="research-article__text-paragraph research-article__text-paragraph-1">
                  This article is published as a preprint (with references)
                  on <a href="https://arxiv.org/abs/2406.06776" style={{"font-weight": 'bold', "text-decoration": 'underline'}}>arXiv</a>.
                </div>
                <div>
                    SeeFar is an evolving collection of multi-resolution satellite images from public and commercial satellites. We specifically
                    curated this dataset for training geospatial foundation models, unconstrained by satellite type. The SeeFar dataset represents a step towards training
                    models that are satellite-agnostic by combining multi-resolution commercial and public access pre-processed images. This will enable
                    users to utilize historical data alongside higher-resolution, more expensive satellite imagery, offering greater flexibility during inference.
                </div>
                <div className="research-article__image-container">
                  <img
                    src={`${CLOUDFRONT_URL}/research-images/seefar/seefar_examples1.png`}
                    alt=""
                    className="research-article__image"
                  />
                  <div className="research-article__image-caption">
                    Figure 1: SeeFar processed data examples from different satellite vendors.
                  </div>
                </div>
                <div className="research-article__text-paragraph research-article__text-paragraph-2">
                    <h1>Introduction</h1>
                    Satellite imagery has revolutionized our understanding of the Earth, providing critical data for a wide range of applications, from&nbsp;
                    <a href="https://ieeexplore.ieee.org/document/10521318" style={{"text-decoration": 'underline'}}>environmental monitoring</a>&nbsp;
                    (<a href="https://arxiv.org/abs/2208.08919" style={{"text-decoration": 'underline'}}>see</a>,&nbsp;
                    <a href="https://www.sciencedirect.com/science/article/abs/pii/S0034425721001322" style={{"text-decoration": 'underline'}}>also</a>,&nbsp;
                    <a href="https://link.springer.com/chapter/10.1007/978-981-10-3713-9_1" style={{"text-decoration": 'underline'}}>and</a>) and&nbsp;
                    <a href="https://www.isprs.org/proceedings/XXXII/part4/meinel149.pdf" style={{"text-decoration": 'underline'}}>urban planning</a>&nbsp;
                    to <a href="https://repository.library.noaa.gov/view/noaa/51288" style={{"text-decoration": 'underline'}}>disaster response</a>&nbsp;
                    and <a href="https://www.sciencedirect.com/science/article/abs/pii/092427169390029M" style={{"text-decoration": 'underline'}}>agricultural management</a>. The
                    ability to capture detailed images of the Earth's surface from space has enabled&nbsp;
                    <a href="https://www.dlib.org/dlib/january11/michener/01michener.html" style={{"text-decoration": 'underline'}}>scientists,</a>&nbsp;
                    <a href="https://www.sciencedirect.com/science/article/pii/S1462901122000338" style={{"text-decoration": 'underline'}}>policymakers</a>,
                    and <a href="https://www.tandfonline.com/doi/full/10.1080/10095020.2017.1333230" style={{"text-decoration": 'underline'}}>other professionals</a>&nbsp;to make
                    well-informed decisions using precise and up-to-date information provided by satellites.
                </div>
                <div className="research-article__text-paragraph research-article__text-paragraph-3">
                    In recent years, technological advancements have&nbsp;
                    <a href="https://link.springer.com/protocol/10.1007/978-1-0716-3790-6_19" style={{"text-decoration": 'underline'}}>dramatically increased</a>&nbsp;the&nbsp;
                    accessibility and quality if satellite imagery. The past five years have&nbsp;
                    <a href="https://www.science.org/doi/10.1126/science.adi4639" style={{"text-decoration": 'underline'}}>seen more earth-observing satellites launched</a>&nbsp;
                    than in the previous fifty, with modern commercial satellites now offering up to&nbsp;
                    <a href="https://link.springer.com/protocol/10.1007/978-1-0716-3790-6_19" style={{"text-decoration": 'underline'}}>100 times</a>&nbsp;the&nbsp;
                    <a href="https://ieeexplore.ieee.org/abstract/document/5408745?signout=success&signout=success" style={{"text-decoration": 'underline'}}>spatial resolution</a>&nbsp;
                    of traditional public access satellites. These advancements have opened up&nbsp;
                    <a href="https://www.iieta.org/journals/ts/paper/10.18280/ts.410226" style={{"text-decoration": 'underline'}}>new possibilities</a>≈;
                    for high-resolution data analysis and application.
                </div>
                <div className="research-article__text-paragraph research-article__text-paragraph-4">
                    Despite these advancements, significant challenges remain. The high cost of commercial satellite imagery (
                    <a href="https://www.planet.com" style={{"text-decoration": 'underline'}}>$4</a>&nbsp;to&nbsp;
                    <a href="https://up42.com/goingup/pleiades-neo" style={{"text-decoration": 'underline'}}>$30</a>&nbsp;
                    per square kilometer) and its limited historical availability pose barriers to the training of foundational
                    geospatial models. The development of such models is important because it will facilitate our understanding of the earth at scale.
                </div>
                <div className="research-article__text-paragraph research-article__text-paragraph-5">
                    Satellite data&nbsp;<a href="https://arxiv.org/abs/2402.01444" style={{"text-decoration": 'underline'}}>is a unique modality</a>.&nbsp;
                    Not only is satellite data temporal, akin to video, but it is also spatially diverse with resolutions varying (as shown in Figure 1)&nbsp;
                    <a href="https://ui.adsabs.harvard.edu/abs/2024EGUGA..2617150Z/abstract" style={{"text-decoration": 'underline'}}>from less than a meter to many kilometers</a>.&nbsp;
                    Unlike conventional 3-channel RGB images in 8-bit depth, which have 256 possible values per pixel, satellite data can consist of
                    multiple channels captured in 12-bit depth, each pixel containing 4096 possible values. This significantly complicates the training
                    of geospatial foundation models. Despite these challenges, there are a growing number of publications that aim to develop machine-learning
                    models using satellite data,&nbsp;
                    <a href="https://arxiv.org/abs/2403.05419" style={{"text-decoration": 'underline'}}>highlighting</a>&nbsp;
                    <a href="https://www.sciencedirect.com/science/article/abs/pii/S1566253524001866" style={{"text-decoration": 'underline'}}>the</a>&nbsp;
                    <a href="https://arxiv.org/abs/2311.17179" style={{"text-decoration": 'underline'}}>importance</a>&nbsp;
                    <a href="https://arxiv.org/abs/2405.01656" style={{"text-decoration": 'underline'}}>of</a>&nbsp;
                    <a href="https://arxiv.org/abs/2207.08051" style={{"text-decoration": 'underline'}}>developing</a>&nbsp;
                    <a href="https://arxiv.org/abs/2403.15356" style={{"text-decoration": 'underline'}}>such</a>&nbsp;
                    <a href="https://arxiv.org/abs/2306.03831" style={{"text-decoration": 'underline'}}>models</a>&nbsp;
                    (see&nbsp;<a href="https://arxiv.org/abs/2401.07527" style={{"text-decoration": 'underline'}}>also</a>).
                </div>
                <div className="research-article__text-paragraph research-article__text-paragraph-6">
                    Additionally, integrating and standardizing data from diverse sources, such as different types of satellites, remains a complex task.
                    Diverse formats can lead to fragmented and inconsistent data, which hinders the development of robust, generalizable models that can
                    effectively utilize data from multiple sources. This inconsistency directly impacts the performance and applicability of these models
                    in real-world scenarios.
                </div>
                <div className="research-article__text-paragraph research-article__text-paragraph-7">
                    Without a dataset that seamlessly integrates historical data with high-resolution imagery, models are less likely to generalize well
                    during inference since the majority of the accessible historical data is of low resolution. This limitation underscores the need for
                    a dataset that bridges the gap between different satellite sources, enhancing the models’ robustness and flexibility.
                </div>
                <div className="research-article__text-paragraph research-article__text-paragraph-9">
                    ... Read more details in the preprint hosted on <a href="https://arxiv.org/abs/2406.06776" style={{"font-weight": 'bold', "text-decoration": 'underline'}}>arXiv</a>.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SeeFar;
